import React from "react";
import { useTranslation } from "react-i18next";

interface PronunciationScoreModalProps {
  said: string;
  score: number;
  onClose: () => void;
}

function ScoreRing({ score }: { score: number }) {
  const { t } = useTranslation();
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const pct = score / 10;
  const offset = circumference * (1 - pct);

  const color =
    score >= 9
      ? "hsl(var(--accent2))"
      : score >= 7
      ? "#6d5aff"
      : score >= 5
      ? "#f59e0b"
      : "hsl(var(--accent3))";

  const label =
    score >= 9
      ? t("pronunciation.excellent")
      : score >= 7
      ? t("pronunciation.good")
      : score >= 5
      ? t("pronunciation.notBad")
      : t("pronunciation.keepPracticing");

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <div style={{ position: "relative", width: 96, height: 96 }}>
        <svg width="96" height="96" style={{ transform: "rotate(-90deg)" }}>
          {/* Track */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke="hsl(var(--bg3))"
            strokeWidth="8"
          />
          {/* Progress */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        {/* Score text */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "Instrument Serif, serif",
              fontSize: 26,
              fontWeight: 700,
              color,
              lineHeight: 1,
            }}
          >
            {score}
          </span>
          <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>/10</span>
        </div>
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color,
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default function PronunciationScoreModal({
  said,
  score,
  onClose,
}: PronunciationScoreModalProps) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "hsl(var(--bg2))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "var(--radius)",
          padding: 32,
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          boxShadow: "0 24px 64px rgba(0,0,0,.5)",
          animation: "fadeInScale .18s ease",
          position: "relative",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "transparent",
            border: "none",
            color: "hsl(var(--muted-foreground))",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: 4,
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "hsl(var(--muted-foreground))",
            marginBottom: 20,
          }}
        >
          🎤 {t("pronunciation.result")}
        </div>

        {/* Score ring */}
        <ScoreRing score={score} />

        {/* Divider */}
        <div
          style={{
            width: "100%",
            height: 1,
            background: "hsl(var(--border))",
            margin: "24px 0",
          }}
        />

        {/* What was said */}
        <div style={{ width: "100%", marginBottom: 24 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "hsl(var(--muted-foreground))",
              marginBottom: 8,
            }}
          >
            {t("pronunciation.youSaid")}:
          </div>
          <div
            style={{
              background: "hsl(var(--bg3))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "var(--radius-sm)",
              padding: "12px 16px",
              fontFamily: "Instrument Serif, serif",
              fontSize: 18,
              fontStyle: "italic",
              color: "hsl(var(--foreground))",
              textAlign: "center",
            }}
          >
            "{said}"
          </div>
        </div>

        {/* Feedback bar */}
        <div style={{ width: "100%", marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              color: "hsl(var(--muted-foreground))",
              marginBottom: 6,
            }}
          >
            <span>{t("pronunciation.accuracy")}</span>
            <span>{score * 10}%</span>
          </div>
          <div
            style={{
              height: 8,
              background: "hsl(var(--bg3))",
              borderRadius: 99,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${score * 10}%`,
                background:
                  score >= 9
                    ? "linear-gradient(90deg, hsl(var(--accent2)), #00e5a0)"
                    : score >= 7
                    ? "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))"
                    : score >= 5
                    ? "linear-gradient(90deg, #f59e0b, #fbbf24)"
                    : "linear-gradient(90deg, hsl(var(--accent3)), #ff5a8a)",
                borderRadius: 99,
                transition: "width 0.6s ease",
              }}
            />
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            width: "100%",
            padding: "12px 20px",
            borderRadius: 50,
            border: "none",
            background: "hsl(var(--primary))",
            color: "white",
            fontFamily: "DM Sans, sans-serif",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all .2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          {t("pronunciation.continue")}
        </button>
      </div>
    </div>
  );
}
